import * as React from 'react';

import { emptyObject } from 'bones/constants/empty';
import { OWL_THING_IRI } from 'bones/constants/iris';
import { MimeType } from 'bones/constants/mimeTypes';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { VoiceboxPanelData } from 'bones/constants/voicebox/panel';
import { VoiceboxSettingsContextValue } from 'bones/constants/voicebox/settings';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { useWithLoading } from 'bones/utils/hooks/useWithLoading';
import { alphaSorter } from 'bones/utils/sort/alphaSort';
import { makeStardogQueryName } from 'bones/utils/voicebox/stardogQuery';
import {
  QueryArgs,
  ResultBindings,
  usePortalVoiceboxStardogQuery,
} from 'bones/utils/voicebox/usePortalVoiceboxStardogQuery';

const getTypesQueryArgs = (
  iri: string
): QueryArgs<ResultBindings<{ type: string }>, string[]> => ({
  query: `\
# ${makeStardogQueryName('Voicebox Types', iri)}
SELECT DISTINCT
  ?type
WHERE {
  <${iri}> a ?type
  FILTER(isIri(?type) && ?type != <${OWL_THING_IRI}>)
}
`,
  queryResultsMime: MimeType.JSON_SPARQL_RESULTS,
  parseBody: async (body) => {
    const bindings = body?.results?.bindings || [];
    return bindings
      .reduce<string[]>((acc, binding) => {
        if (binding.type?.value) {
          acc.push(binding.type.value);
        }

        return acc;
      }, [])
      .sort(alphaSorter);
  },
});

export const usePortalVoiceboxTypesFetcher = (
  connectionIdx: number,
  settingsContext: VoiceboxSettingsContextValue,
  setTypesForIri: (
    iri: string,
    action: React.SetStateAction<VoiceboxPanelData['types']>
  ) => void
) => {
  const { modelsByDatabaseId, settings } = settingsContext;
  const modelsById = modelsByDatabaseId[settings.databaseId] || emptyObject;
  const { classesByIri } = modelsById[settings.model || DEFAULT_MODEL_NAME] || {
    classesByIri: emptyObject,
  };

  const isMounted = useIsMounted();
  const withLoading = useWithLoading('usePortalVoiceboxTypesFetcher');

  const fetchTypes = usePortalVoiceboxStardogQuery(
    connectionIdx,
    settingsContext,
    getTypesQueryArgs
  );

  return React.useCallback(
    async (iri: string) => {
      const isModelConcept = Boolean(classesByIri[iri]);
      if (isModelConcept) {
        return null;
      }

      return withLoading(
        (loading) => setTypesForIri(iri, (prev) => ({ ...prev, loading })),
        async () => {
          const types = await fetchTypes(iri);
          if (!types || !isMounted.current) {
            return null;
          }

          setTypesForIri(iri, (prev) => ({
            ...prev,
            types,
          }));

          return types;
        }
      );
    },
    [classesByIri, fetchTypes, isMounted, setTypesForIri, withLoading]
  );
};
